import { useEffect, useMemo, useState } from 'react';
import { GlobeHemisphereWest } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MasterContentDetailBase from './MasterContentDetailBase';

const LANGUAGES = [
  ['it', 'IT', 'Italiano'],
  ['en', 'EN', 'English'],
  ['es', 'ES', 'Español'],
  ['fr', 'FR', 'Français'],
];

const COPY = {
  it: {
    title: 'Traduci Master', source: 'Originale', translating: 'Traduzione…', stale: 'Da aggiornare',
    success: 'Traduzione pronta', readonly: 'Variante tradotta · torna all’originale per modificare la fonte.',
    editOriginal: 'La variante tradotta è derivata dal Master originale. Torna alla lingua originale per modificarlo.',
    error: 'Impossibile tradurre il Master. Riprova.',
  },
  en: {
    title: 'Translate Master', source: 'Original', translating: 'Translating…', stale: 'Needs update',
    success: 'Translation ready', readonly: 'Translated variant · switch to the original to edit the source.',
    editOriginal: 'The translated variant comes from the original Master. Switch to the original language to edit it.',
    error: 'Could not translate the Master. Try again.',
  },
  es: {
    title: 'Traducir Master', source: 'Original', translating: 'Traduciendo…', stale: 'Actualizar',
    success: 'Traducción lista', readonly: 'Variante traducida · vuelve al original para editar la fuente.',
    editOriginal: 'La variante traducida deriva del Master original. Vuelve al idioma original para editarlo.',
    error: 'No se pudo traducir el Master. Inténtalo de nuevo.',
  },
  fr: {
    title: 'Traduire le Master', source: 'Original', translating: 'Traduction…', stale: 'À mettre à jour',
    success: 'Traduction prête', readonly: 'Variante traduite · revenez à l’original pour modifier la source.',
    editOriginal: 'La variante traduite provient du Master original. Revenez à la langue originale pour le modifier.',
    error: 'Impossible de traduire le Master. Réessayez.',
  },
};

function normalizedLanguage(value) {
  const code = String(value || 'it').toLowerCase().split(/[-_]/)[0];
  return LANGUAGES.some(([id]) => id === code) ? code : 'it';
}

function translatedContent(content, language) {
  const sourceLanguage = normalizedLanguage(content?.content_master?.language || content?.language);
  if (language === sourceLanguage) return content;
  const entry = content?.master_translations?.[language];
  const master = entry?.content_master;
  if (!master) return content;
  return {
    ...content,
    content_master: master,
    master_hash: entry.master_hash,
    title: master.title,
    hook_text: master.primary_hook,
    pillar: master.objective,
    objective: master.objective,
    language,
  };
}

function outputMatchesLanguage(output, language, sourceLanguage) {
  if (!output) return false;
  const tagged = String(output.source_master_language || '').trim().toLowerCase();
  if (tagged) return tagged === language;
  return language === sourceLanguage;
}

function callWithOptionalConfig(fn, url, body, config) {
  return config === undefined ? fn(url, body) : fn(url, body, config);
}

export default function MasterContentDetail(props) {
  const { content, api, onUpdate } = props;
  const [localContent, setLocalContent] = useState(content);
  const sourceLanguage = normalizedLanguage(localContent?.content_master?.language || localContent?.language);
  const [selectedLanguage, setSelectedLanguage] = useState(sourceLanguage);
  const [translating, setTranslating] = useState('');
  const uiLanguage = normalizedLanguage(localStorage.getItem('sketchario_lang') || navigator.language);
  const copy = COPY[uiLanguage] || COPY.it;

  useEffect(() => setLocalContent(content), [content]);

  const isFresh = language => {
    if (language === sourceLanguage) return true;
    const entry = localContent?.master_translations?.[language];
    return Boolean(
      entry?.content_master
      && entry.source_master_revision === localContent?.master_revision
      && entry.source_master_hash === localContent?.master_hash
    );
  };

  useEffect(() => {
    if (selectedLanguage !== sourceLanguage && !isFresh(selectedLanguage)) {
      setSelectedLanguage(sourceLanguage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localContent?.master_revision, localContent?.master_hash, sourceLanguage]);

  const selectLanguage = async language => {
    if (translating) return;
    if (language === sourceLanguage || isFresh(language)) {
      setSelectedLanguage(language);
      return;
    }
    const existing = localContent?.master_translations?.[language];
    setTranslating(language);
    try {
      const { data } = await api.post(`/pro/master-projects/contents/${localContent.id}/translate`, {
        expected_master_revision: localContent.master_revision,
        target_language: language,
        force_refresh: Boolean(existing),
      });
      const entry = data?.translation;
      if (!entry?.content_master) throw new Error('translation_missing');
      const next = {
        ...localContent,
        master_translations: {
          ...(localContent.master_translations || {}),
          [language]: entry,
        },
      };
      setLocalContent(next);
      setSelectedLanguage(language);
      onUpdate?.(next);
      toast.success(copy.success);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === 'string' ? detail : copy.error));
    } finally {
      setTranslating('');
    }
  };

  const localizedApi = useMemo(() => ({
    get: (url, config) => {
      if (selectedLanguage !== sourceLanguage && /\/pro\/master-projects\/contents\/[^/]+\/(?:preview|export)\/(?:html|pdf)/.test(url)) {
        return api.get(url, { ...(config || {}), params: { ...(config?.params || {}), language: selectedLanguage } });
      }
      return config === undefined ? api.get(url) : api.get(url, config);
    },
    post: (url, body, config) => {
      const payload = selectedLanguage !== sourceLanguage && /\/pro\/master-projects\/contents\/[^/]+\/derive\//.test(url)
        ? { ...(body || {}), language: selectedLanguage }
        : body;
      return callWithOptionalConfig(api.post.bind(api), url, payload, config);
    },
    put: (url, body, config) => {
      if (selectedLanguage !== sourceLanguage && /\/pro\/master-projects\/contents\//.test(url)) {
        return Promise.reject({ response: { data: { detail: { message: copy.editOriginal } } } });
      }
      return callWithOptionalConfig(api.put.bind(api), url, body, config);
    },
    delete: (...args) => api.delete(...args),
  }), [api, selectedLanguage, sourceLanguage, copy.editOriginal]);

  const visibleContent = translatedContent(localContent, selectedLanguage);
  const translatedSelected = selectedLanguage !== sourceLanguage;
  const languageToolbar = <div
    className="rounded-xl border border-white/10 bg-[var(--bg-card)] p-3 shadow-lg shadow-black/10"
    data-testid="master-language-toolbar"
  >
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[.12em] text-fuchsia-300">
        <GlobeHemisphereWest size={15} weight="duotone" /> {copy.title}
      </div>
      <div className="flex items-center gap-1">
        {LANGUAGES.map(([id, short, name]) => {
          const source = id === sourceLanguage;
          const fresh = isFresh(id);
          const stale = !source && Boolean(localContent?.master_translations?.[id]) && !fresh;
          const active = selectedLanguage === id;
          return <button
            key={id}
            type="button"
            onClick={() => selectLanguage(id)}
            disabled={Boolean(translating)}
            title={`${name}${source ? ` · ${copy.source}` : stale ? ` · ${copy.stale}` : ''}`}
            data-testid={`master-language-${id}`}
            className={`relative rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition ${active ? 'border-fuchsia-400/60 bg-fuchsia-500/20 text-white' : 'border-white/10 bg-white/[.03] text-slate-300 hover:bg-white/[.08]'}`}
          >
            {translating === id ? '…' : short}
            {!source && !fresh && <span className={`absolute -right-1 -top-1 h-2 w-2 rounded-full ${stale ? 'bg-amber-400' : 'bg-fuchsia-400'}`} />}
          </button>;
        })}
      </div>
    </div>
    {translatedSelected && <p className="mt-2 text-[9px] leading-relaxed text-slate-400" data-testid="master-language-readonly-note">{copy.readonly}</p>}
    {translating && <p className="mt-2 text-[9px] text-fuchsia-300">{copy.translating}</p>}
  </div>;

  return <div className={translatedSelected ? 'master-language-variant-readonly' : ''}>
    <style>{`.master-language-variant-readonly [data-testid="master-detail-edit-btn"]{display:none!important}`}</style>
    <MasterContentDetailBase
      {...props}
      content={visibleContent}
      api={localizedApi}
      actionPanelHeader={languageToolbar}
      onUpdate={updated => {
        const next = { ...localContent, ...updated };
        setLocalContent(next);
        onUpdate?.(next);
      }}
      existingCarousel={outputMatchesLanguage(props.existingCarousel, selectedLanguage, sourceLanguage) ? props.existingCarousel : null}
      existingReel={outputMatchesLanguage(props.existingReel, selectedLanguage, sourceLanguage) ? props.existingReel : null}
      existingPromptedReel={outputMatchesLanguage(props.existingPromptedReel, selectedLanguage, sourceLanguage) ? props.existingPromptedReel : null}
    />
  </div>;
}